/**
 * useReportsDashboard - Data fetching and normalization hook
 * 
 * Handles:
 * - Fetching dashboard data from API
 * - Normalizing data formats (accepts both arrays and objects)
 * - Ensuring all known keys exist with zeros
 * - Safe percentage calculations
 */
import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ACCESS_TOKEN_KEY = 'riskee_access_token';

// Get auth headers for API calls
const getAuthHeaders = () => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Known status values in business order
export const STATUS_ORDER = ['NEW', 'TRIAGED', 'IN_TREATMENT', 'VERIFICATION', 'CLOSED', 'ARCHIVED'];

// Known report types
export const TYPE_ORDER = ['HAZARD', 'NEAR_MISS', 'INCIDENT', 'RISK', 'IMPROVEMENT', 'PSYCHOSOCIAL', 'WELFARE'];

/**
 * Defensive normalizer - converts both arrays and objects to consistent object format
 * @param {Array|Object} input - Raw data from API
 * @param {string} keyField - Field name to use as key (for array format)
 * @param {string} valueField - Field name to use as value (for array format)
 * @returns {Object} Normalized object {key: value}
 */
export function normaliseCounts(input, keyField = 'status', valueField = 'count') {
  if (!input) return {};
  
  if (Array.isArray(input)) {
    // Handle array format: [{status: 'NEW', count: 5}, ...]
    return Object.fromEntries(
      input.map(item => [item[keyField], item[valueField] ?? 0])
    );
  }
  
  // Already an object - return as-is
  return input;
}

/**
 * Ensure all known keys exist in the counts object
 * @param {Object} counts - Normalized counts object
 * @param {Array} knownKeys - Array of keys that should exist
 * @returns {Object} Counts with all keys present (zeros for missing)
 */
export function ensureAllKeys(counts, knownKeys) {
  const result = { ...counts };
  for (const key of knownKeys) {
    if (!(key in result)) {
      result[key] = 0;
    }
  }
  return result;
}

/**
 * Calculate safe percentage (handles division by zero)
 * @param {number} count - Numerator
 * @param {number} total - Denominator
 * @returns {number} Percentage (0-100), 0 if total is 0
 */
export function safePercentage(count, total) {
  const safeTotal = total ?? 0;
  if (safeTotal <= 0) return 0;
  return (count / safeTotal) * 100;
}

/**
 * Get ordered status entries for consistent rendering
 * @param {Object} byStatus - Status counts object
 * @returns {Array} Array of [status, count] in business order
 */
export function getOrderedStatusEntries(byStatus) {
  const normalised = normaliseCounts(byStatus, 'status', 'count');
  const withAllKeys = ensureAllKeys(normalised, STATUS_ORDER);
  return STATUS_ORDER.map(status => [status, withAllKeys[status] ?? 0]);
}

/**
 * Get ordered type entries for consistent rendering
 * @param {Object} byType - Type counts object
 * @returns {Array} Array of [type, count] in business order
 */
export function getOrderedTypeEntries(byType) {
  const normalised = normaliseCounts(byType, 'type', 'count');
  const withAllKeys = ensureAllKeys(normalised, TYPE_ORDER);
  return TYPE_ORDER.map(type => [type, withAllKeys[type] ?? 0]);
}

/**
 * Main hook for fetching and normalizing reports dashboard data
 */
export function useReportsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/reports/dashboard`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`API Error: ${res.status}`);
      
      const raw = await res.json();
      
      // Normalize the data
      const normalised = {
        timestamp: raw.timestamp || new Date().toISOString(),
        scope: raw.scope || 'all_sites',
        summary: {
          total_risks: raw.summary?.total_risks ?? 0,
          open_risks: raw.summary?.open_risks ?? 0,
          critical_high: raw.summary?.critical_high ?? 0,
          overdue_actions: raw.summary?.overdue_actions ?? 0,
          closed_this_week: raw.summary?.closed_this_week ?? 0,
        },
        // Normalize and ensure all keys exist
        by_status: ensureAllKeys(
          normaliseCounts(raw.by_status, 'status', 'count'),
          STATUS_ORDER
        ),
        by_type: ensureAllKeys(
          normaliseCounts(raw.by_type, 'type', 'count'),
          TYPE_ORDER
        ),
        // by_site is now keyed by site_id: {name, count}
        by_site: raw.by_site ?? {},
        recent_risks: raw.recent_risks ?? [],
      };
      
      setData(normalised);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Format timestamp for display
  const formatLastUpdated = () => {
    if (!data?.timestamp) return '';
    const date = new Date(data.timestamp);
    return date.toLocaleDateString('en-AU', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format scope for display
  const formatScope = () => {
    if (!data?.scope) return 'All Sites';
    if (data.scope === 'all_sites') return 'All Sites';
    return data.scope;
  };

  return {
    data,
    loading,
    error,
    refetch: fetchDashboard,
    // Helper getters
    summary: data?.summary ?? {},
    byStatus: data?.by_status ?? {},
    byType: data?.by_type ?? {},
    bySite: data?.by_site ?? {},
    recentRisks: data?.recent_risks ?? [],
    lastUpdated: formatLastUpdated(),
    scope: formatScope(),
    // Utility functions
    getStatusEntries: () => getOrderedStatusEntries(data?.by_status),
    getTypeEntries: () => getOrderedTypeEntries(data?.by_type),
    getSiteEntries: () => Object.entries(data?.by_site ?? {}).map(([id, v]) => ({
      id,
      name: v?.name || id,
      count: v?.count ?? 0
    })),
    safePercentage: (count) => safePercentage(count, data?.summary?.total_risks),
  };
}

export default useReportsDashboard;
